// 1. React and React-related imports
import React, { lazy, Suspense, useState } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';

// 2. Third-party libraries
import { useAuth } from '@clerk/clerk-react';

// 3. Local components
import ProtectedRoute from '@/features-flat/auth/ProtectedRoute';
import { shouldRedirectForAuth, redirectToAuth, isDevelopmentEnvironment } from '@/utils/authRedirect';
import DashboardLayout from '@/layouts/DashboardLayout';
import AuthLayout from '@/layouts/AuthLayout';
import Dashboard from '@/features-flat/dashboard/Dashboard';
import GuestList from '@/features-flat/guests/GuestList';
import GuestDetail from '@/features-flat/guests/GuestDetail';
import GuestForm from '@/features-flat/guests/GuestForm';
import NominationList from '@/features-flat/nominations/NominationList';
import ApplicationList from '@/features-flat/applications/ApplicationList';
import TargetGuestList from '@/features-flat/target-guests/TargetGuestList';
import MapView from '@/features-flat/map/MapView';
import Login from '@/features-flat/auth/Login';
import PublicRouteOnly from '@/features-flat/auth/PublicRouteOnly';
import NotFound from '@/features-flat/error/NotFound';
import Unauthorized from '@/features-flat/error/Unauthorized';
import LoadingScreen from '@/shared/components/feedback/LoadingScreen';
import EpisodeList from '@/features-flat/episodes/EpisodeList';
import SettingsPage from '@/features-flat/settings/Settings';

// Lazy-loaded components
const NominationDetail = lazy(() => import('@/features-flat/nominations/NominationDetail'));
const NominationForm = lazy(() => import('@/features-flat/nominations/NominationForm'));
const ApplicationDetail = lazy(() => import('@/features-flat/applications/ApplicationDetail'));
const ApplicationForm = lazy(() => import('@/features-flat/applications/ApplicationForm'));
const TargetGuestDetail = lazy(() => import('@/features-flat/target-guests/TargetGuestDetail'));
const TargetGuestForm = lazy(() => import('@/features-flat/target-guests/TargetGuestForm'));
const ConvertToGuest = lazy(() => import('@/features-flat/target-guests/ConvertToGuest'));
const GuestSchedule = lazy(() => import('@/features-flat/guests/GuestSchedule'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const TeamManagementPage = lazy(() => import('@/pages/SimpleTeamManagementPage'));
const AdminSetupPage = lazy(() => import('@/pages/AdminSetupPage'));

/**
 * ConvertToGuestWrapper Component
 * 
 * Wrapper for ConvertToGuest to provide required props from route params
 */
const ConvertToGuestWrapper: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const [isOpen, setIsOpen] = useState(true);
  
  const handleClose = () => {
    setIsOpen(false);
    // Navigate back to target guest detail
    window.history.back();
  };
  
  const handleConfirm = async (additionalData: Record<string, unknown>) => {
    // In a real implementation, this would call the API
    console.log('Converting target guest', id, 'with data', additionalData);
    handleClose();
  };
  
  return (
    <ConvertToGuest
      open={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      targetGuestId={id}
    />
  );
};

/**
 * AppRoutes Component
 * 
 * Main routing configuration for the application
 */
const AppRoutes: React.FC = () => {
  const { isSignedIn, isLoaded } = useAuth();
  
  console.log('[AppRoutes] isLoaded:', isLoaded, 'isSignedIn:', isSignedIn);
  
  // Show loading screen while auth is initializing
  if (!isLoaded) {
    return <LoadingScreen />;
  }
  
  // Enhanced redirect logic for unauthenticated users
  if (!isSignedIn) {
    const isPortalDomain = window.location.hostname === 'portal.recurringvaluepodcast.com';
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname.includes('replit') || window.location.hostname.includes('pages.dev');
    
    // If on portal domain and not signed in, redirect to accounts domain
    if (isPortalDomain) {
      console.log('[AppRoutes] Redirecting unauthenticated user to accounts domain');
      
      // Preserve the current path so user can be redirected back after login
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      const returnUrl = encodeURIComponent(`https://portal.recurringvaluepodcast.com${currentPath}`);
      
      // Redirect to accounts domain with return URL
      window.location.href = `https://accounts.recurringvaluepodcast.com?redirect_url=${returnUrl}`;
      
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <LoadingScreen />
            <p className="mt-4 text-gray-600">Redirecting to login...</p>
          </div>
        </div>
      );
    }
    
    // For local development, show a warning but allow access
    if (isLocalDev) {
      console.log('[AppRoutes] Local development - user not authenticated but allowing access');
    }
  }
  
  return (
    <Routes>
      {/* Auth routes */}
      <Route path="/login" element={
        <PublicRouteOnly>
          <AuthLayout>
            <Login />
          </AuthLayout>
        </PublicRouteOnly>
      } />
      
      {/* Protected dashboard routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        
        {/* Guest routes */}
        <Route path="guests">
          <Route index element={<GuestList />} />
          <Route path=":id" element={<GuestDetail />} />
          <Route path="new" element={<GuestForm />} />
          <Route path=":id/edit" element={<GuestForm />} />
          <Route path=":id/schedule" element={
            <Suspense fallback={<LoadingScreen />}>
              <GuestSchedule />
            </Suspense>
          } />
        </Route>
        
        {/* Nomination routes */}
        <Route path="nominations">
          <Route index element={<NominationList />} />
          <Route path=":id" element={
            <Suspense fallback={<LoadingScreen />}>
              <NominationDetail />
            </Suspense>
          } />
          <Route path="new" element={
            <Suspense fallback={<LoadingScreen />}>
              <NominationForm />
            </Suspense>
          } />
          <Route path=":id/edit" element={
            <Suspense fallback={<LoadingScreen />}>
              <NominationForm />
            </Suspense>
          } />
        </Route>
        
        {/* Application routes */}
        <Route path="applications">
          <Route index element={<ApplicationList />} />
          <Route path=":id" element={
            <Suspense fallback={<LoadingScreen />}>
              <ApplicationDetail />
            </Suspense>
          } />
          <Route path="new" element={
            <Suspense fallback={<LoadingScreen />}>
              <ApplicationForm />
            </Suspense>
          } />
          <Route path=":id/edit" element={
            <Suspense fallback={<LoadingScreen />}>
              <ApplicationForm />
            </Suspense>
          } />
        </Route>
        
        {/* Target Guest routes */}
        <Route path="target-guests">
          <Route index element={<TargetGuestList />} />
          <Route path=":id" element={
            <Suspense fallback={<LoadingScreen />}>
              <TargetGuestDetail />
            </Suspense>
          } />
          <Route path="new" element={
            <Suspense fallback={<LoadingScreen />}>
              <TargetGuestForm />
            </Suspense>
          } />
          <Route path=":id/edit" element={
            <Suspense fallback={<LoadingScreen />}>
              <TargetGuestForm />
            </Suspense>
          } />
          <Route path=":id/convert" element={
            <Suspense fallback={<LoadingScreen />}>
              <ConvertToGuestWrapper />
            </Suspense>
          } />
        </Route>
        
        {/* Map view */}
        <Route path="map" element={<MapView />} />
        
        {/* Episodes Route */}
        <Route path="episodes" element={<EpisodeList />} />
        
        {/* Settings Page - Admin Only */}
        <Route 
          path="settings"
          element={ (
            <ProtectedRoute requiredPermission="access:settings">
              <Suspense fallback={<LoadingScreen />}>
                <SettingsPage />
              </Suspense>
            </ProtectedRoute>
          )}
        />
        
        {/* Profile Page */}
        <Route path="profile" element={
          <Suspense fallback={<LoadingScreen />}>
            <ProfilePage />
          </Suspense>
        } />

        {/* Team Management Page */}
        <Route path="team" element={
          <Suspense fallback={<LoadingScreen />}>
            <TeamManagementPage />
          </Suspense>
        } />

        {/* Admin Setup Page */}
        <Route path="admin-setup" element={
          <Suspense fallback={<LoadingScreen />}>
            <AdminSetupPage />
          </Suspense>
        } />
        
        {/* Error routes */}
        <Route path="unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      
      {/* Redirect to login if accessing root while not authenticated */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export default AppRoutes;
